import { useQueryClient } from "@tanstack/react-query";
import { mapToAlert } from "@/pages/human-resources/time-management/endpoints/alerts/inout/ScheduleAlertOut.ts";
import { scheduleAlertsQueryKeys } from "@/pages/human-resources/time-management/endpoints/alerts/SchedulesAlertsQueryKey.ts";
import { readSchedulesAlerts } from "@/pages/human-resources/time-management/endpoints/alerts/SchedulesAlertsEndpoint.ts";
import { ScheduleAlerts } from "@/pages/human-resources/time-management/business/schedule/ScheduleAlerts.ts";
import useSchedulesManager from "@/pages/human-resources/time-management/schedules/site-schedule/model/schedule-data/useSchedulesManager.ts";
import { useState } from "react";
import useShowError from "@/shared/custom-hook/useShowError.ts";
import { useTranslation } from "react-i18next";
import type { EmployeeSchedules } from "@/pages/human-resources/time-management/business/EmployeeSchedules.ts";

export function useFetchSchedulesAlerts(): (
  schedules: EmployeeSchedules[],
) => Promise<ScheduleAlerts | undefined> {
  const queryClient = useQueryClient();
  const scheduleManager = useSchedulesManager();
  const { siteId, startDate, endDate } = scheduleManager.contractsManager;
  const { t } = useTranslation(["humanResources"], {
    keyPrefix: "timeManagement",
  });
  const [fetchError, setFetchError] = useState(false);
  useShowError(fetchError, t("errors.backend.alerts"));

  return async (
    schedules: EmployeeSchedules[],
  ): Promise<ScheduleAlerts | undefined> => {
    const employeesIdsNumbers = schedules.map((es) => parseInt(es.employee.id));
    const alertsOut = await queryClient.fetchQuery({
      retry: false,
      staleTime: 0,
      queryKey: scheduleAlertsQueryKeys.forEmployees(
        siteId,
        startDate,
        endDate,
        employeesIdsNumbers,
      ),
      queryFn: async () => {
        try {
          setFetchError(false);
          return await readSchedulesAlerts(
            siteId,
            employeesIdsNumbers,
            startDate,
            endDate,
          );
        } catch (error: unknown) {
          setFetchError(true);
          throw error;
        }
      },
    });
    return new ScheduleAlerts(alertsOut.map(mapToAlert));
  };
}
